import {
  cdr,
  identityAdmin,
  orchestrator,
  useAuth,
  useCan,
  type AgentRecord,
  type ClientRecord,
  type DidRecord,
  type HoldMusicState,
  type PlatformUser,
  type QueueRecord,
  type RetentionSetting,
} from "@savvy/runtime";
import { Caps } from "@savvy/shared";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@savvy/ui";
import { useCallback, useEffect, useRef, useState } from "react";

import { CallTypesEditor } from "./CallTypesEditor";

/**
 * The admin page. Each section renders only for its capability, and the
 * server enforces the same rules regardless.
 *
 * Clients & DIDs (BACKLOG §12) is the first REAL CRUD here — clients are
 * daily-operations data the call center's own admins manage, which is exactly
 * what this UI is for. Agent/queue PROVISIONING (infrastructure-shaped, done
 * once) stays on the API until a real admin app exists.
 */
export function AdminView() {
  const canAgents = useCan(Caps.TelephonyAgentsManage);
  const canClients = useCan(Caps.TelephonyClientsManage);
  const canSettings = useCan(Caps.TelephonySettingsManage);

  if (!canAgents && !canClients && !canSettings) {
    return <p className="text-muted-foreground">Nothing here needs your attention — this page is for admins.</p>;
  }
  return (
    <div>
      {canClients && <ClientsPanel />}
      {canAgents && <AgentLinksPanel />}
      {canAgents && <TimingPanel />}
      {canSettings && <HoldMusicPanel />}
      {canSettings && <RetentionPanel />}
    </div>
  );
}

// Radix select items cannot carry an empty value; DEFAULT_Q stands in for
// "no override — route to the default queue".
const DEFAULT_Q = "default";

/**
 * Clients & their DIDs (BACKLOG §12). The dialed number decides WHO a call is
 * for: each client has a name, the greeting the agent reads on answer, an
 * optional queue override, and the DIDs it owns. Changes apply on the NEXT
 * inbound call — routing reads the database live, nothing to reload.
 */
function ClientsPanel() {
  const api = useAuth((s) => s.api);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [queues, setQueues] = useState<QueueRecord[]>([]);
  // clientId → which sub-section is open on that row.
  const [openSection, setOpenSection] = useState<Record<string, ClientSection>>({});
  const [dids, setDids] = useState<Record<string, DidRecord[]>>({});
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      setClients(await orchestrator(api).listClients());
      setQueues(await orchestrator(api).listQueues());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [api]);

  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void load();
  }, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const loadDids = async (clientId: string) => {
    try {
      const list = await orchestrator(api).listClientDids(clientId);
      setDids((d) => ({ ...d, [clientId]: list }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const toggleSection = (id: string, want: Exclude<ClientSection, null>) => {
    const next = openSection[id] === want ? null : want;
    setOpenSection((m) => ({ ...m, [id]: next }));
    if (next === "dids") void loadDids(id);
  };

  const defaultQueue = queues.find((q) => q.is_default) ?? queues.find((q) => q.enabled);

  return (
    <section className="mb-8">
      <h2 className="mb-1 text-base font-semibold">Clients</h2>
      <p className="mb-2 text-sm text-muted-foreground">
        Who you answer for. The dialed number picks the client; the agent sees the name and
        greeting on answer. Changes apply on the next call. Calls to a number that belongs to no
        client still ring — into the default queue, flagged “unmapped” in history.
      </p>

      <div className="mb-2 flex flex-wrap items-end gap-1.5 rounded-md border bg-card p-2">
        <Input
          className="w-64"
          placeholder="New client name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={busy || newName.trim() === ""}
          onClick={() =>
            void run(async () => {
              await orchestrator(api).createClient({ name: newName.trim() });
              setNewName("");
            })
          }
        >
          Add client
        </Button>
        {defaultQueue && (
          <span className="text-xs text-muted-foreground">
            Default queue: <strong>{defaultQueue.key}</strong>
            {!defaultQueue.is_default && " (first enabled — none marked default)"}
          </span>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Client</TableHead>
            <TableHead>Greeting (what the agent says)</TableHead>
            <TableHead>Queue</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clients.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground">
                no clients yet — add the first one above, then give it a DID
              </TableCell>
            </TableRow>
          )}
          {clients.map((c) => (
            <ClientRow
              key={c.id}
              client={c}
              queues={queues}
              section={openSection[c.id] ?? null}
              dids={dids[c.id]}
              busy={busy}
              onToggle={(want) => toggleSection(c.id, want)}
              onSave={(patch) => void run(() => orchestrator(api).updateClient(c.id, patch))}
              onDelete={() =>
                void run(async () => {
                  setOpenSection((m) => ({ ...m, [c.id]: null }));
                  await orchestrator(api).deleteClient(c.id);
                })
              }
              onAddDid={(number) =>
                void run(async () => {
                  await orchestrator(api).addClientDid(c.id, number);
                  await loadDids(c.id);
                })
              }
              onRemoveDid={(didId) =>
                void run(async () => {
                  await orchestrator(api).removeDid(didId);
                  await loadDids(c.id);
                })
              }
            />
          ))}
        </TableBody>
      </Table>
      {err && <p className={errText}>{err}</p>}
    </section>
  );
}

/** Which sub-section of a client row is open. Only one at a time: both open
 *  at once turns the table into a wall of inputs. */
type ClientSection = "dids" | "call-types" | null;

function ClientRow({
  client,
  queues,
  section,
  dids,
  busy,
  onToggle,
  onSave,
  onDelete,
  onAddDid,
  onRemoveDid,
}: {
  client: ClientRecord;
  queues: QueueRecord[];
  section: ClientSection;
  dids?: DidRecord[];
  busy: boolean;
  onToggle: (s: Exclude<ClientSection, null>) => void;
  onSave: (patch: Partial<Pick<ClientRecord, "name" | "greeting" | "queue_id" | "enabled">>) => void;
  onDelete: () => void;
  onAddDid: (number: string) => void;
  onRemoveDid: (didId: string) => void;
}) {
  const [name, setName] = useState(client.name);
  const [greeting, setGreeting] = useState(client.greeting);
  const [queueId, setQueueId] = useState(client.queue_id ?? "");
  const [newDid, setNewDid] = useState("");

  const dirty = name !== client.name || greeting !== client.greeting || queueId !== (client.queue_id ?? "");

  return (
    <>
      <TableRow>
        <TableCell>
          <Input className="w-48" value={name} onChange={(e) => setName(e.target.value)} />
        </TableCell>
        <TableCell>
          <Input
            className="w-full min-w-64"
            value={greeting}
            placeholder="Good morning, …, how may I help you?"
            onChange={(e) => setGreeting(e.target.value)}
          />
        </TableCell>
        <TableCell>
          <Select value={queueId || DEFAULT_Q} onValueChange={(v) => setQueueId(v === DEFAULT_Q ? "" : v)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DEFAULT_Q}>Default queue</SelectItem>
              {queues
                .filter((q) => q.enabled)
                .map((q) => (
                  <SelectItem key={q.id} value={q.id}>
                    {q.key}
                    {q.is_default ? " (default)" : ""}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell className="space-x-1.5 whitespace-nowrap">
          <Button
            variant="outline"
            size="sm"
            disabled={busy || !dirty || name.trim() === ""}
            onClick={() => onSave({ name: name.trim(), greeting, queue_id: queueId })}
          >
            Save
          </Button>
          <Button
            variant={section === "dids" ? "default" : "outline"}
            size="sm"
            onClick={() => onToggle("dids")}
          >
            DIDs{dids ? ` (${dids.length})` : ""}
          </Button>
          <Button
            variant={section === "call-types" ? "default" : "outline"}
            size="sm"
            onClick={() => onToggle("call-types")}
          >
            Call types
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={onDelete}>
            Delete
          </Button>
        </TableCell>
      </TableRow>
      {section === "call-types" && (
        <TableRow>
          <TableCell colSpan={4} className="bg-card">
            <CallTypesEditor clientId={client.id} />
          </TableCell>
        </TableRow>
      )}
      {section === "dids" && (
        <TableRow>
          <TableCell colSpan={4} className="bg-card">
            <div className="flex flex-wrap items-center gap-1.5 py-1">
              {(dids ?? []).map((d) => (
                <span key={d.id} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-sm">
                  <span className="font-mono">{d.number}</span>
                  <button
                    className="text-muted-foreground hover:text-danger"
                    title="Unmap this number (its calls go to the default queue, flagged)"
                    onClick={() => onRemoveDid(d.id)}
                  >
                    ×
                  </button>
                </span>
              ))}
              <Input
                className="w-44"
                placeholder="+15551234567"
                value={newDid}
                onChange={(e) => setNewDid(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newDid.trim() !== "") {
                    onAddDid(newDid.trim());
                    setNewDid("");
                  }
                }}
              />
              <Button
                variant="outline"
                size="sm"
                disabled={busy || newDid.trim() === ""}
                onClick={() => {
                  onAddDid(newDid.trim());
                  setNewDid("");
                }}
              >
                Add DID
              </Button>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/** One agent's editable timing draft, as strings so the inputs stay honest. */
type TimingDraft = { ring: string; wrap: string; misses: string };

function draftOf(a: AgentRecord): TimingDraft {
  return {
    ring: String(a.ring_seconds),
    wrap: String(a.wrap_up_time),
    misses: String(a.max_no_answer),
  };
}

// Queue timing: the mod_callcenter knobs every agent carries. Per-agent is
// what the switch stores; "apply to selected/all" is the floor-wide
// interaction the operator asked for — batch writes over the same values.
function TimingPanel() {
  const api = useAuth((s) => s.api);
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [drafts, setDrafts] = useState<Record<string, TimingDraft>>({});
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [batch, setBatch] = useState<TimingDraft>({ ring: "", wrap: "", misses: "" });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await orchestrator(api).listAgents();
      setAgents(list);
      setDrafts(Object.fromEntries(list.map((a) => [a.id, draftOf(a)])));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [api]);

  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void load();
  }, [load]);

  // A blank field is OMITTED from the PATCH (the server leaves it unchanged),
  // so a floor-wide "wrap-up = 10" does not force values into the other two.
  // Filled fields are checked against the same ranges the database enforces —
  // otherwise the first out-of-range agent aborts a batch halfway through.
  const parse = (d: TimingDraft): { patch?: Record<string, number>; error?: string } => {
    const limits = [
      { raw: d.ring, min: 1, max: 300, api: "ring_seconds", label: "Ring (s)" },
      { raw: d.wrap, min: 0, max: 3600, api: "wrap_up_time", label: "Wrap-up (s)" },
      { raw: d.misses, min: 1, max: 100, api: "max_no_answer", label: "Max misses" },
    ];
    const patch: Record<string, number> = {};
    for (const { raw, min, max, api, label } of limits) {
      if (raw.trim() === "") continue;
      const n = Number(raw);
      if (!Number.isInteger(n) || n < min || n > max) {
        return { error: `${label} must be a whole number from ${min} to ${max}` };
      }
      patch[api] = n;
    }
    if (Object.keys(patch).length === 0) return { error: "fill at least one value" };
    return { patch };
  };

  const saveOne = async (a: AgentRecord) => {
    const { patch, error } = parse(drafts[a.id] ?? draftOf(a));
    if (!patch) {
      setErr(error ?? "invalid timing values");
      return;
    }
    setBusy(true);
    setNote(null);
    setErr(null);
    try {
      await orchestrator(api).updateAgent(a.id, patch);
      setNote(`${a.extension} saved — the switch picks it up on the next reconcile pass.`);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const applyBatch = async (targets: AgentRecord[]) => {
    const { patch, error } = parse(batch);
    if (!patch) {
      setErr(error ?? "fill at least one batch value");
      return;
    }
    setBusy(true);
    setNote(null);
    setErr(null);
    try {
      for (const a of targets) {
        await orchestrator(api).updateAgent(a.id, patch);
      }
      setNote(`Applied to ${targets.length} agent${targets.length === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const selected = agents.filter((a) => checked[a.id]);
  const numInput = (value: string, onChange: (v: string) => void) => (
    <Input
      type="number"
      min={0}
      step={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-20"
    />
  );

  return (
    <section className="mb-8">
      <h2 className="mb-1 text-base font-semibold">Queue timing</h2>
      <p className="mb-2 text-sm text-muted-foreground">
        Per agent: how long their phone rings before the queue moves on, the wrap-up window after
        each call before the queue offers another (after-call work — not a break, the queue just
        skips them), and how many missed calls trigger the automatic pause.
      </p>

      <div className="mb-2 flex flex-wrap items-end gap-1.5 rounded-md border bg-card p-2">
        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
          Ring (s){numInput(batch.ring, (v) => setBatch({ ...batch, ring: v }))}
        </div>
        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
          Wrap-up (s){numInput(batch.wrap, (v) => setBatch({ ...batch, wrap: v }))}
        </div>
        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
          Max misses{numInput(batch.misses, (v) => setBatch({ ...batch, misses: v }))}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={busy || selected.length === 0}
          onClick={() => void applyBatch(selected)}
        >
          Apply to selected ({selected.length})
        </Button>
        <Button variant="outline" size="sm" disabled={busy || agents.length === 0} onClick={() => void applyBatch(agents)}>
          Apply to all ({agents.length})
        </Button>
        <span className="text-xs text-muted-foreground">
          Blank = keep each agent&apos;s current value. Ranges: ring 1–300, wrap-up 0–3600, misses 1–100.
        </span>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead></TableHead>
            <TableHead>Extension</TableHead>
            <TableHead>Ring (s)</TableHead>
            <TableHead>Wrap-up (s)</TableHead>
            <TableHead>Max misses</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {agents.map((a) => {
            const d = drafts[a.id] ?? draftOf(a);
            const setD = (patch: Partial<TimingDraft>) =>
              setDrafts({ ...drafts, [a.id]: { ...d, ...patch } });
            return (
              <TableRow key={a.id}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={!!checked[a.id]}
                    onChange={(e) => setChecked({ ...checked, [a.id]: e.target.checked })}
                  />
                </TableCell>
                <TableCell>
                  {a.extension}
                  {a.display_name ? ` · ${a.display_name}` : ""}
                </TableCell>
                <TableCell>{numInput(d.ring, (v) => setD({ ring: v }))}</TableCell>
                <TableCell>{numInput(d.wrap, (v) => setD({ wrap: v }))}</TableCell>
                <TableCell>{numInput(d.misses, (v) => setD({ misses: v }))}</TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" disabled={busy} onClick={() => void saveOne(a)}>
                    Save
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {note && <p className="mt-2 text-sm text-success">{note}</p>}
      {err && <p className={errText}>{err}</p>}
    </section>
  );
}

const errText = "rounded-md bg-danger-bg px-2.5 py-1.5 text-sm text-danger";

// Radix select items cannot carry an empty value; NONE stands in for
// "unlinked" and is translated back to "" at the API edge.
const NONE = "none";

function AgentLinksPanel() {
  const api = useAuth((s) => s.api);
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [users, setUsers] = useState<PlatformUser[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      setAgents(await orchestrator(api).listAgents());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      return;
    }
    try {
      setUsers(await identityAdmin(api).listUsers());
    } catch {
      // A telephony admin without identity-admin rights: show links read-only.
      setUsers(null);
    }
  }, [api]);

  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void load();
  }, [load]);

  const userName = (id?: string) => {
    if (!id) return "";
    const u = users?.find((u) => u.id === id);
    return u ? u.username : `${id.slice(0, 8)}…`;
  };

  const link = async (agent: AgentRecord, userId: string) => {
    setBusy(agent.id);
    setNote(null);
    setErr(null);
    try {
      await orchestrator(api).updateAgent(agent.id, { identity_user_id: userId });
      setNote(
        userId
          ? `${agent.extension} linked to ${userName(userId)} — they can sign in at /agent/ now`
          : `${agent.extension} unlinked`,
      );
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="mb-8">
      <h2 className="mb-1 text-base font-semibold">Agents &amp; app users</h2>
      <p className="mb-2 text-sm text-muted-foreground">
        Linking a platform user to an agent is what lets that user sign in to the agent app at{" "}
        <span className="font-mono">/agent/</span>. One user, one agent.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Extension</TableHead>
            <TableHead>Enabled</TableHead>
            <TableHead>App user</TableHead>
            {users && <TableHead></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {agents.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground">
                no agents provisioned
              </TableCell>
            </TableRow>
          )}
          {agents.map((a) => (
            <TableRow key={a.id}>
              <TableCell>
                {a.extension}
                {a.display_name ? ` · ${a.display_name}` : ""}
              </TableCell>
              <TableCell>{a.enabled ? "yes" : "no"}</TableCell>
              <TableCell>
                {userName(a.identity_user_id) || <span className="text-muted-foreground">not linked</span>}
              </TableCell>
              {users && (
                <TableCell>
                  <LinkControl
                    agent={a}
                    users={users}
                    disabled={busy === a.id}
                    onLink={(userId) => void link(a, userId)}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!users && agents.length > 0 && (
        <p className="mt-2 text-sm text-muted-foreground">
          Links are read-only for you — changing them needs identity admin rights.
        </p>
      )}
      {note && <p className="mt-2 text-sm text-success">{note}</p>}
      {err && <p className={errText}>{err}</p>}
    </section>
  );
}

function LinkControl({
  agent,
  users,
  disabled,
  onLink,
}: {
  agent: AgentRecord;
  users: PlatformUser[];
  disabled: boolean;
  onLink: (userId: string) => void;
}) {
  const [selected, setSelected] = useState(agent.identity_user_id ?? "");
  return (
    <div className="flex items-center gap-1.5">
      <Select value={selected || NONE} onValueChange={(v) => setSelected(v === NONE ? "" : v)}>
        <SelectTrigger className="w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>— none —</SelectItem>
          {users
            .filter((u) => !u.is_disabled)
            .map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.username}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled || selected === (agent.identity_user_id ?? "")}
        onClick={() => onLink(selected)}
      >
        {selected ? "Link" : "Unlink"}
      </Button>
    </div>
  );
}

function HoldMusicPanel() {
  const api = useAuth((s) => s.api);
  const [state, setState] = useState<HoldMusicState | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setState(await orchestrator(api).holdMusic());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [api]);

  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void load();
  }, [load]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setErr("choose a file first");
      return;
    }
    setBusy(true);
    setNote(null);
    setErr(null);
    try {
      const res = await orchestrator(api).uploadHoldMusic(file);
      setNote(
        res.stream_reloaded
          ? `Uploaded as ${res.filename} — plays on the next consult.`
          : `Stored as ${res.filename}, but the stream reload FAILED — restart FreeSWITCH to apply.`,
      );
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setNote(null);
    setErr(null);
    try {
      await orchestrator(api).deleteHoldMusic();
      setNote("Removed — callers hear generated ringback again.");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-8">
      <h2 className="mb-1 text-base font-semibold">Hold music</h2>
      <p className="mb-2 text-sm text-muted-foreground">
        What a parked caller hears during an Announce consult. No file uploaded = generated
        ringback. WAV or MP3, judged by content, max 25 MB; the very next consult plays it.
      </p>
      <p className="mb-2 text-sm">
        {state?.configured ? (
          <>
            Configured: <strong>{state.filename}</strong> ·{" "}
            {((state.size_bytes ?? 0) / 1048576).toFixed(1)} MB · uploaded {state.uploaded_at}
          </>
        ) : (
          <span className="text-muted-foreground">Not configured — callers hear generated ringback.</span>
        )}
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        <Input ref={fileRef} type="file" accept=".wav,.mp3,audio/wav,audio/mpeg" className="h-auto w-72 py-1" />
        <Button variant="outline" size="sm" disabled={busy} onClick={() => void upload()}>
          Upload
        </Button>
        {state?.configured && (
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void remove()}>
            Remove (back to ringback)
          </Button>
        )}
      </div>
      {note && <p className="mt-2 text-sm text-success">{note}</p>}
      {err && <p className={errText}>{err}</p>}
    </section>
  );
}

function RetentionPanel() {
  const api = useAuth((s) => s.api);
  const [setting, setSetting] = useState<RetentionSetting | null>(null);
  const [days, setDays] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void (async () => {
      try {
        const s = await cdr(api).recordingRetention();
        setSetting(s);
        setDays(String(s.days));
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [api]);

  const save = async () => {
    const n = Number(days);
    if (!Number.isInteger(n) || n < 0) {
      setErr("days must be a whole number, 0 or more");
      return;
    }
    setBusy(true);
    setNote(null);
    setErr(null);
    try {
      const s = await cdr(api).setRecordingRetention(n);
      setSetting(s);
      setNote(
        s.keeps_forever
          ? "Saved — recordings are kept forever."
          : `Saved — audio older than ${s.days} days is removed on the next hourly pass. Call history is never deleted.`,
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-8">
      <h2 className="mb-1 text-base font-semibold">Recording retention</h2>
      <p className="mb-2 text-sm text-muted-foreground">
        How long recorded call AUDIO is kept. 0 keeps everything. Call history rows are never
        deleted — an expired recording shows as "recording expired" in History. Changes apply
        within the hour, no restart.
      </p>
      {setting && (
        <p className="mb-2 text-sm">
          Current:{" "}
          <strong>{setting.keeps_forever ? "keep forever" : `${setting.days} days`}</strong>
          {setting.source === "default" && (
            <span className="text-muted-foreground"> · install default (no admin policy set yet)</span>
          )}
        </p>
      )}
      <div className="flex items-center gap-1.5">
        <Input
          type="number"
          min={0}
          step={1}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="w-24"
        />
        <span className="text-sm text-muted-foreground">days</span>
        <Button variant="outline" size="sm" disabled={busy || !setting} onClick={() => void save()}>
          Save
        </Button>
      </div>
      {note && <p className="mt-2 text-sm text-success">{note}</p>}
      {err && <p className={errText}>{err}</p>}
    </section>
  );
}
